// Single global bottom-sheet mounted at the top of `GuestMapPage`. Any
// "Get directions" button anywhere in the map surface triggers it via the
// module-scoped bus in `./directionsIntent`.

import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MapPin, Navigation, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  notifyVenueEnRoute,
  openDirectionsMaps,
  openLocationOnlyMaps,
  subscribeDirectionsIntent,
} from './directionsIntent';

const font = "'Inria Serif', serif";

export function DirectionsIntentModal() {
  const [pendingVenue, setPendingVenue] = useState(null);

  useEffect(() => subscribeDirectionsIntent(setPendingVenue), []);

  const close = () => setPendingVenue(null);
  const venueName = pendingVenue?.name || 'the venue';

  const handleNotify = () => {
    const v = pendingVenue;
    // Fire the ping; when the backend replies `throttled: true` (guest
    // tapped again within the 30-min cooldown for this same venue), show
    // a small toast so the guest gets closure instead of the modal just
    // closing silently. Maps still opens either way.
    if (v?.id) {
      notifyVenueEnRoute(v.id).then((result) => {
        if (result?.throttled) {
          toast('Already pinged!', {
            duration: 3500,
            id: `en-route-throttled-${v.id}`,
          });
        }
      });
    }
    close();
    // Defer Maps launch a tick so the modal-close animation isn't jumped
    // over by the OS handing focus to the maps app.
    setTimeout(() => openDirectionsMaps(v), 60);
  };

  const handleLocationOnly = () => {
    const v = pendingVenue;
    close();
    setTimeout(() => openLocationOnlyMaps(v), 60);
  };

  return (
    <AnimatePresence>
      {pendingVenue ? (
        <motion.div
          key="directions-intent-backdrop"
          data-testid="directions-intent-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 10800,
            background: 'rgba(10, 12, 20, 0.62)', backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            onClick={(event) => event.stopPropagation()}
            style={{
              // `position: relative` is critical — it makes this sheet the
              // containing block for the absolutely-positioned close button
              // below. Without it, the X anchors to the nearest positioned
              // ancestor (the full-screen backdrop) and ends up over the
              // phone's status bar instead of at the top of the sheet.
              position: 'relative',
              width: '100%', maxWidth: 460,
              background: 'var(--surface)',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              padding: '18px 20px calc(24px + env(safe-area-inset-bottom)) 20px',
              boxShadow: '0 -18px 60px rgba(10, 12, 20, 0.35)',
            }}
          >
            <div style={{ width: 44, height: 5, borderRadius: 999, background: 'var(--card-border)', margin: '0 auto 14px' }} />

            <button
              type="button"
              onClick={close}
              aria-label="Close"
              data-testid="directions-intent-close-btn"
              style={{
                position: 'absolute', top: 12, right: 14,
                width: 32, height: 32, borderRadius: '50%',
                border: 'none', cursor: 'pointer',
                background: 'var(--accent-surface)', color: 'var(--text-2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={16} />
            </button>

            <p style={{ margin: 0, fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', letterSpacing: '0.005em' }}>
              Heading to {venueName}?
            </p>

            <button
              type="button"
              onClick={handleNotify}
              data-testid="directions-intent-notify-btn"
              style={{
                marginTop: 16, width: '100%', padding: '14px 16px',
                borderRadius: 20, border: 'none', cursor: 'pointer',
                background: 'var(--accent-purple, #5544CC)', color: '#fff',
                fontFamily: font, fontSize: 15.5, fontWeight: 700,
                boxShadow: '0 10px 24px rgba(85, 68, 204, 0.28)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Navigation size={16} strokeWidth={2.4} />
              Get Directions Ready
            </button>

            <button
              type="button"
              onClick={handleLocationOnly}
              data-testid="directions-intent-location-only-btn"
              style={{
                marginTop: 10, width: '100%', padding: '13px 16px',
                borderRadius: 20, cursor: 'pointer',
                background: 'transparent',
                color: 'var(--accent-purple, #5544CC)',
                fontFamily: font, fontSize: 14.5, fontWeight: 700,
                border: '1.5px solid var(--accent-purple, #5544CC)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <MapPin size={16} strokeWidth={2.4} />
              See Location Only
            </button>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
